/**
 * Security utilities for input validation and XSS protection
 */

import { z } from 'zod';

// Common validation schemas
export const addressSchema = z.string().min(32).max(44).regex(/^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/, {
  message: 'Invalid Solana address format'
});

export const amountSchema = z.number().min(0.000001).max(1000000).positive({
  message: 'Amount must be between 0.000001 and 1,000,000'
});

export const slippageSchema = z.number().min(0.1).max(50).positive({
  message: 'Slippage must be between 0.1% and 50%'
});

export const tokenNameSchema = z.string().min(1).max(50).regex(/^[a-zA-Z0-9\s\-_]+$/, {
  message: 'Token name must be 1-50 characters, alphanumeric only'
});

export const tokenSymbolSchema = z.string().min(2).max(10).regex(/^[A-Z]+$/, {
  message: 'Token symbol must be 2-10 uppercase letters'
});

export const emailSchema = z.string().email().max(255).optional();
export const urlSchema = z.string().url().max(2048).optional();

// Security validation functions
export class SecurityValidator {
  /**
   * Sanitize HTML content to prevent XSS
   */
  static sanitizeHtml(input: string): string {
    if (!input) return '';
    
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  /**
   * Validate and sanitize user input
   */
  static sanitizeInput(input: string, maxLength = 1000): string {
    if (!input) return '';
    
    // Remove null bytes and control characters
    let sanitized = input.replace(/[\x00-\x1F\x7F]/g, '');
    
    // Trim and limit length
    sanitized = sanitized.trim().substring(0, maxLength);
    
    return sanitized;
  }

  /**
   * Validate Solana address format
   */
  static isValidSolanaAddress(address: string): boolean {
    try {
      return addressSchema.safeParse(address).success;
    } catch {
      return false;
    }
  }

  /**
   * Validate transaction amount
   */
  static validateAmount(amount: number, balance?: number): { valid: boolean; error?: string } {
    const result = amountSchema.safeParse(amount);
    
    if (!result.success) {
      return { valid: false, error: result.error.errors[0].message };
    }
    
    if (balance && amount > balance) {
      return { valid: false, error: 'Amount exceeds available balance' };
    }
    
    return { valid: true };
  }

  /**
   * Check for common attack patterns
   */
  static containsAttackPatterns(input: string): boolean {
    const attackPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*<\/script>|[\s\S]*?<\/script>)/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /data:text\/html/gi,
      /vbscript:/gi,
      /expression\s*\(/gi,
      /url\s*\(/gi,
      /@import/gi,
    ];

    return attackPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Rate limiting validation
   */
  static checkRateLimit(
    identifier: string, 
    maxRequests: number = 100, 
    windowMs: number = 60000
  ): { allowed: boolean; remainingRequests: number; resetTime: number } {
    const now = Date.now();
    const key = `rate_limit_${identifier}`;
    
    // Get existing requests from localStorage (in production, use Redis)
    const existingData = localStorage.getItem(key);
    let requests: number[] = [];
    
    if (existingData) {
      try {
        requests = JSON.parse(existingData);
      } catch {
        requests = [];
      }
    }
    
    // Filter out old requests
    const cutoff = now - windowMs;
    requests = requests.filter(timestamp => timestamp > cutoff);
    
    // Check if under limit
    if (requests.length >= maxRequests) {
      const oldestRequest = Math.min(...requests);
      return {
        allowed: false,
        remainingRequests: 0,
        resetTime: oldestRequest + windowMs
      };
    }
    
    // Add current request
    requests.push(now);
    localStorage.setItem(key, JSON.stringify(requests));
    
    return {
      allowed: true,
      remainingRequests: maxRequests - requests.length,
      resetTime: now + windowMs
    };
  }

  /**
   * Content Security Policy header recommendations
   */
  static getCSPHeaders(): Record<string, string> {
    return {
      'Content-Security-Policy': [
        "default-src 'self'",
        "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://vercel.app",
        "style-src 'self' 'unsafe-inline'",
        "img-src 'self' data: https: http:",
        "connect-src 'self' https://api.mainnet-beta.solana.com https://api.devnet.solana.com ws://localhost:* wss://api.solana.com",
        "font-src 'self'",
        "object-src 'none'",
        "media-src 'self'",
        "frame-src 'none'",
        "child-src 'none'",
        "worker-src 'self'",
        "form-action 'self'",
        "frame-ancestors 'none'",
        "base-uri 'self'",
        "manifest-src 'self'",
        "upgrade-insecure-requests"
      ].join('; '),
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      'Permissions-Policy': 'camera=(), microphone=(), geolocation=()'
    };
  }

  /**
   * Validate API request with signature
   */
  static validateApiRequest(
    payload: any,
    signature: string,
    publicKey: string
  ): boolean {
    try {
      // In production, verify cryptographic signature
      // For now, implement basic structure validation
      if (!payload || !signature || !publicKey) {
        return false;
      }

      // Check for replay attacks
      const timestamp = payload.timestamp;
      if (!timestamp || (Date.now() - timestamp) > 300000) { // 5 minutes
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * SQL Injection prevention
   */
  static sanitizeForDatabase(input: string): string {
    if (!input) return '';
    
    return input
      .replace(/['"\\]/g, '\\\\$&') // Escape quotes and backslashes
      .replace(/--/g, '--') // Remove SQL comments
      .replace(/\/\*/g, '/\\*') // Escape block comments
      .replace(/\*\//g, '*\\/') // Escape block comments
      .replace(/;/g, '\\;') // Escape semicolons
      .trim();
  }

  /**
   * Path traversal prevention
   */
  static sanitizePath(input: string): string {
    if (!input) return '';
    
    return input
      .replace(/\.\./g, '') // Remove parent directory references
      .replace(/[\/\\]/g, '_') // Replace path separators
      .replace(/^[\/\\]/, '') // Remove leading path separators
      .trim();
  }
}

// React Hook for security validation
export const useSecurityValidation = () => {
  const validateInput = (input: string, type: 'text' | 'address' | 'amount' = 'text') => {
    const sanitized = SecurityValidator.sanitizeInput(input);
    
    if (SecurityValidator.containsAttackPatterns(sanitized)) {
      throw new Error('Input contains potentially dangerous content');
    }

    switch (type) {
      case 'address':
        if (!SecurityValidator.isValidSolanaAddress(sanitized)) {
          throw new Error('Invalid Solana address format');
        }
        break;
      
      case 'amount':
        const amount = parseFloat(sanitized);
        const validation = SecurityValidator.validateAmount(amount);
        if (!validation.valid) {
          throw new Error(validation.error || 'Invalid amount');
        }
        break;
    }

    return sanitized;
  };

  const checkRateLimit = (identifier: string, maxRequests?: number) => {
    return SecurityValidator.checkRateLimit(identifier, maxRequests);
  };

  return {
    validateInput,
    checkRateLimit,
    sanitizeHtml: SecurityValidator.sanitizeHtml,
    sanitizeForDatabase: SecurityValidator.sanitizeForDatabase,
    sanitizePath: SecurityValidator.sanitizePath,
  };
};

// Middleware for Express.js (if using Node.js backend)
export const securityMiddleware = {
  // CSRF protection
  csrf: (req: any, res: any, next: any) => {
    const token = req.headers['x-csrf-token'];
    const sessionToken = req.session?.csrfToken;
    
    if (!token || token !== sessionToken) {
      return res.status(403).json({ error: 'Invalid CSRF token' });
    }
    
    next();
  },

  // Rate limiting
  rateLimit: (maxRequests: number, windowMs: number) => {
    const requests = new Map<string, number[]>();
    
    return (req: any, res: any, next: any) => {
      const identifier = req.ip || req.headers['x-forwarded-for'];
      const now = Date.now();
      
      const userRequests = requests.get(identifier) || [];
      const cutoff = now - windowMs;
      
      const validRequests = userRequests.filter(timestamp => timestamp > cutoff);
      
      if (validRequests.length >= maxRequests) {
        return res.status(429).json({
          error: 'Too many requests',
          retryAfter: Math.ceil((cutoff + windowMs - now) / 1000)
        });
      }
      
      validRequests.push(now);
      requests.set(identifier, validRequests);
      
      res.set({
        'X-RateLimit-Limit': maxRequests.toString(),
        'X-RateLimit-Remaining': Math.max(0, maxRequests - validRequests.length).toString(),
        'X-RateLimit-Reset': new Date(cutoff + windowMs).toISOString()
      });
      
      next();
    };
  }
};

// Export security validation schemas for Zod
export const securitySchemas = {
  addressSchema,
  amountSchema,
  slippageSchema,
  tokenNameSchema,
  tokenSymbolSchema,
  emailSchema,
  urlSchema,
};